/**
 * Error Logging Service
 * Centralized error logging and aggregation
 */

import { ERROR_CODES } from '../utils/AppError';

// ============================================================================
// CONFIGURATION
// ============================================================================

const LOG_LEVELS = {
    DEBUG: 0,
    INFO: 1,
    WARN: 2,
    ERROR: 3,
    FATAL: 4
};

const isDev = (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') ||
    (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.Capacitor));

// In-memory error store (for aggregation)
const errorStore = {
    errors: [],
    maxErrors: 100
};

// ============================================================================
// ERROR CONTEXT
// ============================================================================

/**
 * Get current context for error logging
 */
const getContext = () => {
    const context = {
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null
    };

    // Add user info if available
    try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            const user = JSON.parse(storedUser);
            context.userId = user.id;
            context.userEmail = user.email?.substring(0, 3) + '***'; // Mask email
        }
    } catch {
        // Ignore
    }

    return context;
};

// ============================================================================
// LOGGING FUNCTIONS
// ============================================================================

/**
 * Log an error
 */
export const logError = (error, additionalContext = {}) => {
    const entry = {
        level: 'ERROR',
        error: {
            name: error.name || 'Error',
            code: error.code || ERROR_CODES.UNKNOWN,
            message: error.message,
            technicalMessage: error.technicalMessage,
            stack: isDev ? error.stack : undefined
        },
        context: {
            ...getContext(),
            ...additionalContext
        },
        id: generateErrorId()
    };

    // Console output (dev)
    if (isDev) {
        console.error(`[${entry.error.code}] ${entry.error.message}`, error);
    }

    // Store for aggregation
    storeError(entry);

    return entry.id;
};

/**
 * Log a warning
 */
export const logWarning = (message, context = {}) => {
    const entry = {
        level: 'WARN',
        message,
        context: {
            ...getContext(),
            ...context
        },
        timestamp: new Date().toISOString()
    };

    if (isDev) {
        console.warn(`[WARN] ${message}`, context);
    }

    storeError(entry);
};

/**
 * Log info
 */
export const logInfo = (message, context = {}) => {
    if (isDev) {
        console.info(`[INFO] ${message}`, context);
    }
};

// ============================================================================
// ERROR AGGREGATION
// ============================================================================

/**
 * Store error for aggregation
 */
const storeError = (entry) => {
    errorStore.errors.push(entry);

    // Keep only recent errors
    if (errorStore.errors.length > errorStore.maxErrors) {
        errorStore.errors = errorStore.errors.slice(-errorStore.maxErrors);
    }
};

/**
 * Get stored errors
 */
export const getStoredErrors = (limit = 50) => {
    return errorStore.errors.slice(-limit);
};

/**
 * Get error statistics
 */
export const getErrorStats = () => {
    const errors = errorStore.errors;
    const stats = {
        total: errors.length,
        byCode: {},
        byLevel: {},
        lastHour: 0
    };

    const oneHourAgo = Date.now() - 3600000;

    for (const error of errors) {
        // By code
        const code = error.error?.code || 'UNKNOWN';
        stats.byCode[code] = (stats.byCode[code] || 0) + 1;

        // By level
        const level = error.level || 'ERROR';
        stats.byLevel[level] = (stats.byLevel[level] || 0) + 1;

        // Last hour
        if (new Date(error.context?.timestamp).getTime() > oneHourAgo) {
            stats.lastHour++;
        }
    }

    return stats;
};

/**
 * Clear error store
 */
export const clearErrors = () => {
    errorStore.errors = [];
};

// ============================================================================
// ERROR REPORTING (FUTURE: SEND TO BACKEND)
// ============================================================================

/**
 * Report error to backend (placeholder for future implementation)
 */
export const reportError = async (error, context = {}) => {
    const errorId = logError(error, context);

    // Future: Send to error tracking service
    // try {
    //     await fetch('/api/errors', {
    //         method: 'POST',
    //         headers: { 'Content-Type': 'application/json' },
    //         body: JSON.stringify({ errorId, error, context })
    //     });
    // } catch {
    //     // Ignore reporting failures
    // }

    return errorId;
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Generate unique error ID
 */
const generateErrorId = () => {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Check if error should be reported
 */
export const shouldReport = (error) => {
    // Don't report network errors when offline
    if (!navigator.onLine && error.name === 'NetworkError') {
        return false;
    }

    // Don't report user cancellations
    if (error.name === 'AbortError') {
        return false;
    }

    return true;
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
    logError,
    logWarning,
    logInfo,
    getStoredErrors,
    getErrorStats,
    clearErrors,
    reportError,
    shouldReport
};
